import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useBoolean } from 'usehooks-ts';
import { DialogContentText, Grid2 } from '@mui/material';

import { API_DATE_FORMAT, formatDate, getTomorrowDate } from 'helper/DateLib';
import { notifyApiError } from 'helper/apiErrorNotification';
import { useDialog } from 'hooks/useDialog';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { useUpdateRecipientMutation } from 'store/entities/feedbackFramework/api';
import { DatePicker } from 'components/simple';
import { Dialog } from 'components/complex';

const styles = {
  dialogVisible: {
    overflow: 'visible',
  },
  pickerContainer: ({ breakpoints }) => ({
    [breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  }),
};

const dialogId = 'edit-recipient-deadline-dialog';
const tomorrow = getTomorrowDate();

export const EditFrameworkRecipientDeadlineDialog = () => {
  const { closeDialog } = useDialog();
  const { recipientIds, feedbackId, isPersonal, onSuccess } = useSelector(dialogPropsSelector);
  const [date, setDate] = useState(tomorrow);
  const [updateRecipient] = useUpdateRecipientMutation();
  const { value: isLoading, setTrue: startLoading, setFalse: stopLoading } = useBoolean();

  const handleConfirmDate = useCallback(async () => {
    try {
      startLoading();
      await Promise.all(
        recipientIds.map((id) =>
          updateRecipient({
            id,
            feedbackId,
            data: { date: formatDate(date, API_DATE_FORMAT) },
          }).unwrap(),
        ),
      );

      if (onSuccess) {
        onSuccess();
      }

      closeDialog();
    } catch (error) {
      notifyApiError(error);
      stopLoading();
    }
  }, [closeDialog, date, feedbackId, onSuccess, recipientIds, startLoading, stopLoading, updateRecipient]);

  return (
    <Dialog
      isLoading={isLoading}
      id={dialogId}
      onConfirm={handleConfirmDate}
      confirmLabel="Save & Launch"
      cancelLabel="Go Back"
      title="What is the new deadline?"
      customStyles={{
        contentStyles: styles.dialogVisible,
        paperStyles: styles.dialogVisible,
      }}
    >
      <Grid2 container justifyContent="space-between">
        <Grid2 size={{ xs: 12, md: 8 }}>
          <DialogContentText id={`${dialogId}-description`} variant="body2" color="text.primary" mb={4}>
            {isPersonal
              ? 'Modifying the deadline will automatically notify nominees who haven’t submitted feedback about the new deadline date.'
              : `By selecting a new deadline this will notify ${
                  recipientIds.length > 1 ? 'recipients' : 'the recipient'
                } and any relevant nominees of the new deadline date.`}
          </DialogContentText>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }} sx={styles.pickerContainer}>
          <DatePicker minDate={tomorrow} value={date} placeholder="dd/mm/yyyy" onChange={(value) => setDate(value)} />
        </Grid2>
      </Grid2>
    </Dialog>
  );
};
